import type { AssessmentSection } from '@app/src/types/resourceExplorer'
import { ExpandLess, ExpandMore, Star, StarBorder, StarHalf } from '@mui/icons-material'
import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { SelectionStatus } from './types'

type SectionRowProps = {
  section: AssessmentSection
  status: SelectionStatus
  isExpanded: boolean
  onToggleExpand: () => void
  onToggleStar: () => void
  isFirst?: boolean
  children: React.ReactNode
}

const StarIcon: React.FC<{ status: SelectionStatus }> = ({ status }) => {
  if (status === SelectionStatus.All) return <Star />
  if (status === SelectionStatus.Some) return <StarHalf />
  return <StarBorder />
}

const SectionRow: React.FC<SectionRowProps> = ({
  section,
  status,
  isExpanded,
  onToggleExpand,
  onToggleStar,
  isFirst = false,
  children,
}) => {
  const { formatMessage } = useIntl()
  return (
    <Box sx={{ bgcolor: isExpanded ? 'grey.100' : 'background.paper' }}>
      {!isFirst && <Divider />}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
        <IconButton
          onClick={onToggleStar}
          aria-label={formatMessage({
            id:
              status === SelectionStatus.All
                ? 'form.createAutomation.highlightImportantKpis.unstarSection'
                : 'form.createAutomation.highlightImportantKpis.starSection',
          })}
          sx={{ color: status === SelectionStatus.None ? 'action.active' : 'primary.main' }}
        >
          <StarIcon status={status} />
        </IconButton>
        <Stack
          direction="row"
          alignItems="center"
          onClick={onToggleExpand}
          role="button"
          aria-expanded={isExpanded}
          aria-label={formatMessage({
            id: 'form.createAutomation.highlightImportantKpis.toggleSectionAriaLabel',
          })}
          sx={{ flexGrow: 1, cursor: 'pointer', minHeight: 40 }}
        >
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {section.name}
          </Typography>
          <IconButton component="div" tabIndex={-1} aria-hidden sx={{ pointerEvents: 'none' }}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={isExpanded}>
        <Box sx={{ px: 1, pb: 2, pt: 1 }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

export default SectionRow
